import * as d3 from "d3";
import { useRef, useEffect } from "react";

const TimeSeriesChart = ({ 
  data, 
  dimensions = [800, 400], 
  xLabel = "Time", 
  yLabel = "Value",
  xKey = "date",
  yKey = "value",
  groupKey, // Key to group data by (for multiple lines)
  lineColor = "#69b3a2"
}) => {
  const graphContainerRef = useRef(null);
  const graphNodeRef = useRef(null);

  const createGraph = () => {
    // Clear previous graph if any
    if (graphNodeRef.current && graphContainerRef.current.contains(graphNodeRef.current)) {
      graphContainerRef.current.removeChild(graphNodeRef.current);
    }

    if (!data || data.length === 0) return;

    const [width, height] = dimensions;
    const margin = {top: 20, right: 30, bottom: 50, left: 60};
    const w = width - margin.left - margin.right;
    const h = height - margin.top - margin.bottom;

    // Create SVG
    const svg = d3.create("svg")
      .attr("width", width)
      .attr("height", height);

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Parse data and convert dates
    const processedData = data.map(d => ({
      ...d,
      [xKey]: typeof d[xKey] === 'string' && d[xKey].startsWith('Day ') ? 
        parseInt(d[xKey].replace('Day ', '')) : 
        (typeof d[xKey] === 'number' ? d[xKey] : new Date(d[xKey])),
      [yKey]: +d[yKey]
    })).sort((a, b) => a[xKey] - b[xKey]);

    // Group data if groupKey is provided
    let groupedData = {};
    if (groupKey) {
      groupedData = d3.group(processedData, d => d[groupKey]);
    } else {
      groupedData = new Map([['default', processedData]]);
    }

    // Create color scale for multiple lines
    const colorScale = d3.scaleOrdinal(d3.schemeCategory10);

    // Determine scale type and domain
    const isNumericX = typeof processedData[0][xKey] === 'number';
    let x;
    
    if (isNumericX) {
      x = d3.scaleLinear()
        .domain(d3.extent(processedData, d => d[xKey]))
        .range([0, w]);
    } else {
      x = d3.scaleTime()
        .domain(d3.extent(processedData, d => d[xKey]))
        .range([0, w]);
    }

    g.append("g")
      .attr("transform", `translate(0,${h})`)
      .call(d3.axisBottom(x)
        .tickFormat(isNumericX ? d3.format("d") : d3.timeFormat("%Y-%m-%d")));

    // X axis label
    svg.append("text")
      .attr("text-anchor", "middle")
      .attr("x", margin.left + w / 2)
      .attr("y", height - 10)
      .attr("font-size", "14px")
      .text(xLabel);

    // Y axis
    const maxY = d3.max(processedData, d => d[yKey]);
    const y = d3.scaleLinear()
      .domain([0, maxY + 1])
      .range([h, 0]);

    g.append("g")
      .call(d3.axisLeft(y)
        .tickFormat(d3.format("d"))
        .ticks(Math.min(maxY + 1, 10)));

    // Y axis label
    svg.append("text")
      .attr("text-anchor", "middle")
      .attr("transform", `rotate(-90)`)
      .attr("x", -margin.top - h / 2)
      .attr("y", 18)
      .attr("font-size", "14px")
      .text(yLabel);

    // Create line generator
    const line = d3.line()
      .x(d => x(d[xKey]))
      .y(d => y(d[yKey]))
      .curve(d3.curveMonotoneX);

    // Add lines for each group
    groupedData.forEach((groupData, groupName) => {
      const color = groupKey ? colorScale(groupName) : lineColor;
      
      // Add the line
      g.append("path")
        .datum(groupData)
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 2)
        .attr("class", `line-${groupName}`)
        .attr("d", line);

      // Add dots for data points
      g.selectAll(`.dot-${groupName}`)
        .data(groupData)
        .enter().append("circle")
        .attr("class", `dot dot-${groupName}`)
        .attr("cx", d => x(d[xKey]))
        .attr("cy", d => y(d[yKey]))
        .attr("r", 4)
        .attr("fill", color)
        .attr("stroke", "white")
        .attr("stroke-width", 2);
    });

    // Add legend if there are multiple groups
    if (groupKey && groupedData.size > 1) {
      const legend = g.append("g")
        .attr("class", "legend")
        .attr("transform", `translate(${w - 150}, 20)`);

      let legendY = 0;
      groupedData.forEach((groupData, groupName) => {
        const color = colorScale(groupName);
        
        legend.append("rect")
          .attr("x", 0)
          .attr("y", legendY)
          .attr("width", 12)
          .attr("height", 12)
          .attr("fill", color);
        
        legend.append("text")
          .attr("x", 18)
          .attr("y", legendY + 9)
          .attr("font-size", "12px")
          .text(groupName);
        
        legendY += 18;
      });
    }

    // Add hover functionality
    const tooltip = d3.select("body").append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("background", "rgba(0, 0, 0, 0.8)")
      .style("color", "white")
      .style("padding", "8px")
      .style("border-radius", "4px")
      .style("font-size", "12px")
      .style("pointer-events", "none")
      .style("opacity", 0);

    // Add invisible overlay for mouse tracking
    g.append("rect")
      .attr("width", w)
      .attr("height", h)
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .on("mousemove", function(event) {
        const [mouseX] = d3.pointer(event);
        const xValue = x.invert(mouseX);
        
        // Find closest data point across all groups
        let closestPoint = null;
        let minDistance = Infinity;
        
        groupedData.forEach((groupData, groupName) => {
          const bisect = d3.bisector(d => d[xKey]).left;
          const index = bisect(groupData, xValue, 1);
          const d0 = groupData[index - 1];
          const d1 = groupData[index];
          const d = d1 && (xValue - d0[xKey] > d1[xKey] - xValue) ? d1 : d0;
          
          if (d) {
            const distance = Math.abs(xValue - d[xKey]);
            if (distance < minDistance) {
              minDistance = distance;
              closestPoint = { ...d, groupName };
            }
          }
        });

        if (closestPoint) {
          // Highlight the closest point
          g.selectAll(".dot")
            .attr("r", d => (d[xKey] === closestPoint[xKey] && d[yKey] === closestPoint[yKey]) ? 6 : 4)
            .attr("opacity", d => (d[xKey] === closestPoint[xKey] && d[yKey] === closestPoint[yKey]) ? 1 : 0.7);

          // Show tooltip
          tooltip.transition()
            .duration(200)
            .style("opacity", .9);
          
          const xDisplay = isNumericX ? 
            `Día ${closestPoint[xKey]}` : 
            d3.timeFormat("%Y-%m-%d")(closestPoint[xKey]);
          
          tooltip.html(`${xLabel}: ${xDisplay}<br/>${yLabel}: ${closestPoint[yKey]}${groupKey ? `<br/>Grupo: ${closestPoint.groupName}` : ''}`)
            .style("left", (event.pageX + 10) + "px")
            .style("top", (event.pageY - 28) + "px");
        }
      })
      .on("mouseout", function() {
        // Reset all points
        g.selectAll(".dot")
          .attr("r", 4)
          .attr("opacity", 1);

        // Hide tooltip
        tooltip.transition()
          .duration(500)
          .style("opacity", 0)
          .remove();
      });

    graphNodeRef.current = svg.node();
    graphContainerRef.current.appendChild(graphNodeRef.current);
  };

  useEffect(() => {
    if (data && data.length > 0) {
      createGraph();
    }
  }, [data, dimensions, xLabel, yLabel, xKey, yKey, groupKey, lineColor]);

  return (
    <div 
      ref={graphContainerRef} 
      style={{
        width: '100%',
        minHeight: '200px'
      }}
    />
  );
};

export default TimeSeriesChart;
